// Joana chat shell — which appearance the browser draws (light, dark, or the
// machine's).
//
// ## The TypeScript twin of `Appearance.ThemeChoice`
//
// The stored value, the three choices and what an unrecognised value means are
// the shared module's decisions
// (`mobile/shared/.../domain/Appearance.kt`), and this file repeats them for
// the web the way `i18n/index.ts` repeats `InterfaceLanguage.resolve`. The key
// and the encoding are IDENTICAL on purpose: a person who reads about the
// setting on their phone and then looks at the browser should find the same
// three words meaning the same three things.
//
// ## It is stored per device, and that is a decision, not an oversight
//
// The dossier for this session said an appearance choice lives on the account,
// which is ADR-0028's rule for the interface LANGUAGE. The shared module says
// the opposite for appearance, and gives the reason: which appearance suits a
// screen is a property of that screen — its brightness, the room, whether the
// OS is already dark — and somebody with a bright desktop and a dark phone
// wants two different answers. Syncing it would be a decision to make on
// purpose, with an override. So the web matches the phones: local, and only
// local.

/** The three choices, in the order the picker offers them. */
export type ThemeChoice = "system" | "light" | "dark";

/** Where the choice lives. The same key the phones use. */
export const THEME_KEY = "appearance.theme";

/** Read a stored choice. Anything unrecognised — an older build's value, a
 *  truncated write, nothing at all — reads as `system`, because failing to a
 *  working appearance beats failing to none. */
export function decodeTheme(stored: string | null | undefined): ThemeChoice {
  return stored === "light" || stored === "dark" ? stored : "system";
}

/**
 * The value for `<html data-appearance>`, or null to remove the attribute.
 *
 * Null for `system` and not the string "system": the generated CSS keys the
 * dark palette off `prefers-color-scheme` UNLESS an explicit attribute is
 * present, so following the machine means having no attribute at all. Writing
 * `data-appearance="system"` would land in neither branch and pin the page to
 * light on a dark machine.
 */
export function themeAttribute(choice: ThemeChoice): string | null {
  return choice === "system" ? null : choice;
}
